import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, ThumbsUp, X, CheckCircle, ChevronDown } from "lucide-react";

const CATEGORIES = [
  { key: "went_well", label: "✅ Went Well", color: "border-[#00F5D4]/30 bg-[#00F5D4]/5" },
  { key: "improve", label: "🔧 To Improve", color: "border-amber-500/30 bg-amber-500/5" },
  { key: "action", label: "⚡ Actions", color: "border-blue-500/30 bg-blue-500/5" },
];

// ── Lessons Learned Tab ───────────────────────────────────────────────────────

function LessonsTab() {
  const [retros, setRetros] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeRetro, setActiveRetro] = useState(null);
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ project_id: "", title: "", date: new Date().toISOString().slice(0, 10) });
  const [newText, setNewText] = useState({ went_well: "", improve: "", action: "" });
  const [team, setTeam] = useState([]);

  const load = useCallback(async () => {
    try {
      const [rRes, pRes, tRes] = await Promise.all([
        api.get("/retros"),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/team/members").catch(() => ({ data: [] })),
      ]);
      setRetros(rRes.data || []);
      setProjects(pRes.data || []);
      setTeam(Array.isArray(tRes.data) ? tRes.data : []);
    } catch {
      toast.error("Failed to load retros");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openRetro = async (r) => {
    try {
      const res = await api.get(`/retros/${r.retro_id}`);
      setActiveRetro(res.data);
      setItems(res.data.items || []);
    } catch {
      toast.error("Failed to load retro");
    }
  };

  const createRetro = async () => {
    if (!form.title || !form.date) { toast.error("Title and date required"); return; }
    try {
      await api.post("/retros", form);
      toast.success("Retro created");
      setShowCreate(false);
      setForm({ project_id: "", title: "", date: new Date().toISOString().slice(0, 10) });
      load();
    } catch {
      toast.error("Failed to create retro");
    }
  };

  const addItem = async (category) => {
    const text = newText[category].trim();
    if (!text || !activeRetro) return;
    try {
      const res = await api.post(`/retros/${activeRetro.retro_id}/items`, { category, text });
      setItems(prev => [...prev, res.data]);
      setNewText(v => ({ ...v, [category]: "" }));
    } catch {
      toast.error("Failed to add item");
    }
  };

  const vote = async (item) => {
    try {
      const res = await api.post(`/retros/${activeRetro.retro_id}/items/${item.retro_item_id}/vote`);
      setItems(prev => prev.map(i => i.retro_item_id === item.retro_item_id ? res.data : i));
    } catch { /* silent */ }
  };

  const deleteItem = async (item) => {
    try {
      await api.delete(`/retros/${activeRetro.retro_id}/items/${item.retro_item_id}`);
      setItems(prev => prev.filter(i => i.retro_item_id !== item.retro_item_id));
    } catch {
      toast.error("Failed to delete item");
    }
  };

  const updateAction = async (item, assignee_id, due_date) => {
    try {
      const res = await api.put(`/retros/${activeRetro.retro_id}/items/${item.retro_item_id}`, { assignee_id, due_date });
      setItems(prev => prev.map(i => i.retro_item_id === item.retro_item_id ? res.data : i));
    } catch { /* silent */ }
  };

  const closeRetro = async () => {
    try {
      await api.post(`/retros/${activeRetro.retro_id}/close`);
      setActiveRetro(r => ({ ...r, status: "closed" }));
      toast.success("Retro closed");
      load();
    } catch {
      toast.error("Failed to close retro");
    }
  };

  if (activeRetro) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => setActiveRetro(null)} className="text-xs text-[#7d8590] hover:text-white mb-1 flex items-center gap-1">
              <ChevronDown className="w-3 h-3 rotate-90" /> All retros
            </button>
            <h2 className="text-xl font-bold text-white">{activeRetro.title}</h2>
            <p className="text-sm text-[#7d8590] mt-1">{activeRetro.date} · <span className={`capitalize ${activeRetro.status === "closed" ? "text-[#7d8590]" : "text-[#00F5D4]"}`}>{activeRetro.status}</span></p>
          </div>
          {activeRetro.status === "open" && (
            <Button onClick={closeRetro} variant="outline" className="border-[#30363d] text-[#7d8590] hover:text-white">
              <CheckCircle className="w-4 h-4 mr-1" /> Close retro
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATEGORIES.map(cat => {
            const catItems = items.filter(i => i.category === cat.key).sort((a, b) => b.votes - a.votes);
            return (
              <div key={cat.key} className={`border rounded-lg p-4 ${cat.color}`}>
                <div className="font-semibold text-white text-sm mb-4">{cat.label} <span className="text-[#7d8590] font-normal">({catItems.length})</span></div>
                {catItems.map(item => (
                  <div key={item.retro_item_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-3 mb-2 group">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-white flex-1">{item.text}</p>
                      <button onClick={() => deleteItem(item)} className="opacity-0 group-hover:opacity-100 text-[#484f58] hover:text-rose-400 shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <button onClick={() => vote(item)} className="flex items-center gap-1 text-xs text-[#7d8590] hover:text-[#00F5D4]">
                        <ThumbsUp className="w-3 h-3" /> {item.votes}
                      </button>
                      {cat.key === "action" && (
                        <div className="flex items-center gap-2">
                          <select value={item.assignee_id || ""} onChange={e => updateAction(item, e.target.value, item.due_date)}
                            className="text-xs bg-[#161b22] border border-[#30363d] text-[#7d8590] rounded px-1.5 py-0.5">
                            <option value="">Assign…</option>
                            {team.map(m => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                          </select>
                          <input type="date" value={item.due_date || ""} onChange={e => updateAction(item, item.assignee_id, e.target.value)}
                            className="text-xs bg-[#161b22] border border-[#30363d] text-[#7d8590] rounded px-1.5 py-0.5" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {activeRetro.status === "open" && (
                  <div className="flex gap-2 mt-3">
                    <input value={newText[cat.key]} onChange={e => setNewText(v => ({ ...v, [cat.key]: e.target.value }))}
                      onKeyDown={e => e.key === "Enter" && addItem(cat.key)} placeholder="Add a note…"
                      className="flex-1 bg-[#0d1117] border border-[#21262d] text-white rounded-md px-3 py-1.5 text-xs focus:outline-none focus:border-[#00F5D4]/50" />
                    <button onClick={() => addItem(cat.key)} className="p-1.5 bg-[#1c2128] rounded-md text-[#7d8590] hover:text-white border border-[#30363d]">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#7d8590]">Sprint and project lessons learned</p>
        <Button onClick={() => setShowCreate(true)} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Retro
        </Button>
      </div>

      {retros.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">No retrospectives yet. Create your first one.</div>
      ) : (
        <div className="space-y-3">
          {retros.map(r => (
            <button key={r.retro_id} onClick={() => openRetro(r)}
              className="w-full bg-[#0d1117] border border-[#21262d] rounded-lg p-4 text-left hover:border-[#30363d] transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">{r.title}</div>
                  <div className="text-xs text-[#7d8590] mt-1">{r.date} · {r.item_count || 0} items</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${r.status === "closed" ? "border-[#30363d] text-[#7d8590] bg-[#161b22]" : "border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10"}`}>
                  {r.status}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Retrospective</h2>
              <button onClick={() => setShowCreate(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Title *</label>
                <input value={form.title} onChange={e => setForm(v => ({ ...v, title: e.target.value }))}
                  placeholder="e.g. Sprint 12 Retro"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Project (optional)</label>
                <select value={form.project_id} onChange={e => setForm(v => ({ ...v, project_id: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">None</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Date *</label>
                <input type="date" value={form.date} onChange={e => setForm(v => ({ ...v, date: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={createRetro} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">Create</Button>
              <Button onClick={() => setShowCreate(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Retros() {
  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Retrospectives</h1>
        <p className="text-sm text-[#7d8590] mt-1">Sprint and project lessons learned</p>
      </div>
      <LessonsTab />
    </div></DarkPage>
  );
}
